import React from "react";

import { SUBTITLES_OFF } from "../../constants";
import { useEmbedParametersContext } from "../../contexts/embedParametersContext";
import { upperCaseFirstLetter } from "../../helpers/commonUtils";
import i18n from "../../service/i18n";
import { usePlayerStore } from "../../store/usePlayerStore";
import { Subtitles } from "../../types/player-api";
import { MenuHeader } from "../settings/MenuHeader";
import {
  Divider,
  InputLabel,
  SettingInput,
  SettingMenu,
  SettingOption
} from "../settings/styles";
import { TopLevelSettingOption } from "../settings/TopLevelSettings";

const subtitlesDescription = (code?: string, language?: string): string => {
  switch (code) {
    case "fi":
      return "Käännöstekstitys";
    case "fih":
      return "Ohjelmatekstitys";
    case "en":
      return "Subtitles";
    case "sma":
      return "Saame";
    case "smi":
      return "Pohjoissaame";
    case "off":
      return i18n("pois", { language });
    default:
      return i18n("tuntematon", { language });
  }
};

const isMatchingSubtitles = (s1, s2): boolean =>
  !!(s1 && s2 && s1.label === s2.label && s1.language === s2.language);

const subtitlesActive = (
  subtitles: Subtitles[],
  selectedSubtitles?: Subtitles
) =>
  !!(
    selectedSubtitles &&
    subtitles.some(s => isMatchingSubtitles(s, selectedSubtitles))
  );

type OptionsProps = {
  onClickBack: () => void;
  onBlur: (event) => void;
};
export const SubtitlesOptions = ({ onBlur, onClickBack }: OptionsProps) => {
  const { useStoreState } = usePlayerStore();
  const selectedSubtitles = useStoreState.selectedSubtitles();
  const subtitles = useStoreState.subtitles();
  const uiSize = useStoreState.uiSize();

  const setUserPreferredSubtitles = useStoreState.setUserPreferredSubtitles();

  const { language } = useEmbedParametersContext();

  return (
    <>
      <MenuHeader backToTopLevel={onClickBack} label="tekstitykset" />
      <Divider />
      <SettingMenu role="menu">
        {subtitles.map((item, i) => (
          <SettingOption key={i + item.language}>
            <SettingInput
              checked={
                subtitlesActive(subtitles, selectedSubtitles) &&
                isMatchingSubtitles(selectedSubtitles, item)
              }
              id={`subtitles-setting-${item.language}-${i}`}
              name="subtitles"
              onBlur={onBlur}
              onChange={() => setUserPreferredSubtitles(item)}
              onClick={onClickBack}
            />
            <InputLabel
              htmlFor={`subtitles-setting-${item.language}-${i}`}
              uiSize={uiSize}>
              {upperCaseFirstLetter(
                item.label || subtitlesDescription(item.language, language)
              )}
            </InputLabel>
          </SettingOption>
        ))}
        <SettingOption key="off">
          <SettingInput
            checked={!subtitlesActive(subtitles, selectedSubtitles)}
            id="subtitles-setting-off"
            name="subtitles"
            onBlur={onBlur}
            onChange={() => setUserPreferredSubtitles(SUBTITLES_OFF)}
            onClick={onClickBack}
          />
          <InputLabel htmlFor="subtitles-setting-off" uiSize={uiSize}>
            {subtitlesDescription("off", language)}
          </InputLabel>
        </SettingOption>
      </SettingMenu>
    </>
  );
};

type TopLevelOptionProps = {
  autoFocus: boolean;
  onBlur: (event: React.FocusEvent<HTMLElement>) => void;
  onClick: () => void;
};
export const SubtitlesTopLevelOption = ({
  autoFocus,
  onBlur,
  onClick
}: TopLevelOptionProps) => {
  const { useStoreState } = usePlayerStore();
  const selectedSubtitles = useStoreState.selectedSubtitles();
  const subtitles = useStoreState.subtitles();
  const { language } = useEmbedParametersContext();

  return (
    <TopLevelSettingOption
      autoFocus={autoFocus}
      onBlur={onBlur}
      onClick={onClick}
      title={i18n("tekstitykset", { language })}
      value={
        (selectedSubtitles &&
          subtitlesActive(subtitles, selectedSubtitles) &&
          (selectedSubtitles.label ||
            subtitlesDescription(selectedSubtitles.language, language))) ||
        subtitlesDescription("off", language)
      }
    />
  );
};
